// Push notification helper
import { push } from './api';
import toast from 'react-hot-toast';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export async function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function getPushPermission() {
  if (!await isPushSupported()) return 'unsupported';
  return Notification.permission;
}

export async function subscribePush() {
  if (!await isPushSupported()) {
    toast.error('Push not supported on this device');
    return false;
  }
  
  try {
    // Request permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      toast.error('Permission denied');
      return false;
    }
    
    // Register service worker
    const registration = await navigator.serviceWorker.register('/push-sw.js');
    await navigator.serviceWorker.ready;
    
    // Get VAPID key
    const { publicKey } = await push.vapidKey();
    if (!publicKey) {
      toast.error('Server not configured for push');
      return false;
    }
    
    // Subscribe
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }
    
    // Send to backend
    const subData = subscription.toJSON();
    await push.subscribe({
      endpoint: subData.endpoint,
      keys: subData.keys,
      platform: 'web',
      device_info: {
        ua: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
      },
    });
    
    toast.success('🔔 Push notifications enabled!');
    return true;
  } catch(e) {
    console.error('Push subscribe error:', e);
    toast.error('Failed to enable push: ' + e.message);
    return false;
  }
}

export async function unsubscribePush() {
  try {
    const registration = await navigator.serviceWorker.getRegistration('/push-sw.js');
    if (!registration) return false;
    
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      const subData = subscription.toJSON();
      await push.unsubscribe({ endpoint: subData.endpoint });
      await subscription.unsubscribe();
    }
    
    toast.success('Push notifications disabled');
    return true;
  } catch(e) {
    toast.error('Failed: ' + e.message);
    return false;
  }
}

export async function testPush() {
  try {
    const r = await push.test();
    if (r.delivered_to > 0) {
      toast.success(`Sent to ${r.delivered_to} device(s)`);
    } else {
      toast.error('No active devices');
    }
  } catch(e) { toast.error(e.message); }
}
